import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState } from '@/tools/tool.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { BaseToolPersistedState, ToolRehydratePayload } from '@/tools/tool.types';

export type ManualConditionState = BaseToolPersistedState & {
  /** Color of the output condition and preview capsules. */
  color: string;
};

export type ManualConditionDehydratedState = Pick<
  ManualConditionState,
  'id' | 'name' | 'originalParameters' | 'maximumDuration' | 'advancedParametersCollapsed' | 'configVersion' | 'color'
>;

type ManualConditionConfigState = Pick<ManualConditionState, 'configVersion' | 'color'>;

type ManualConditionActions = {
  setSearchName: (name: string) => void;
  setColor: (color: string) => void;
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  reset: ResettableStoreState<ManualConditionState>;
};

const initialState: ManualConditionState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  color: '',
});

export function getManualConditionConfigParams(
  state: ManualConditionConfigState,
): ManualConditionConfigState & { type: string } {
  return {
    type: TREND_TOOLS.MANUAL_CONDITION,
    configVersion: state.configVersion,
    color: state.color,
  };
}

export function dehydrateManualConditionState(state: ManualConditionState): ManualConditionDehydratedState {
  return {
    id: state.id,
    name: state.name,
    originalParameters: state.originalParameters,
    maximumDuration: state.maximumDuration,
    advancedParametersCollapsed: state.advancedParametersCollapsed,
    configVersion: state.configVersion,
    color: state.color,
  };
}

const manualConditionActionsFactory: ActionsFactory<ManualConditionState, ManualConditionActions> = (
  set,
  _get,
  resetState,
) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),
  setColor: (color) =>
    set((draft) => {
      draft.color = color;
    }),
  rehydrateForEdit: (payload) =>
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        currentAdvancedParametersCollapsed: initialState.advancedParametersCollapsed,
      });
      Object.assign(draft, initialState, rehydrationState.config, {
        originalParameters: rehydrationState.originalParameters,
      });
    }),
  reset: resetState,
});

const STORE_NAME = 'ManualCondition';
const [ManualConditionStoreProvider, useManualConditionStoreInstance, useManualConditionStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  manualConditionActionsFactory,
);

export { ManualConditionStoreProvider };

const manualConditionConfigParamsSelector = createSelector(
  [(state: ManualConditionState) => state.configVersion, (state: ManualConditionState) => state.color],
  (configVersion, color) => getManualConditionConfigParams({ configVersion, color }),
);

export const useManualConditionId = () => useManualConditionStoreSelector((state) => state.id);
export const useManualConditionName = () => useManualConditionStoreSelector((state) => state.name);
export const useManualConditionOriginalParameters = () =>
  useManualConditionStoreSelector((state) => state.originalParameters);
export const useManualConditionMaximumDuration = () =>
  useManualConditionStoreSelector((state) => state.maximumDuration);
export const useManualConditionAdvancedParametersCollapsed = () =>
  useManualConditionStoreSelector((state) => state.advancedParametersCollapsed);
export const useManualConditionConfigVersion = () => useManualConditionStoreSelector((state) => state.configVersion);
export const useManualConditionColor = () => useManualConditionStoreSelector((state) => state.color);
export const useManualConditionConfigParams = () =>
  useManualConditionStoreSelector(manualConditionConfigParamsSelector);
export const useManualConditionActions = () => useManualConditionStoreInstance().actions;

/** @deprecated Only for the legacy singleton bridge during Flux migration. */
export function legacyCreateManualConditionStore(initialStateOverrides?: Partial<ManualConditionState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    manualConditionActionsFactory,
    { storeName: STORE_NAME },
  );
}

export const usePersistManualConditionStore = () => {
  const register = useRegisterPersistedStore();
  const store = useManualConditionStoreInstance();
  useEffect(
    () =>
      register({
        storeName: 'sqManualConditionStore',
        persistenceLevel: 'WORKSHEET',
        store,
        dehydrate: dehydrateManualConditionState,
      }),
    [register, store],
  );
};
